import React, { useMemo, useState } from "react";
import {
  Plus, Trash2, Clock, Copy, ChevronDown, ChevronUp, Check,
  CalendarClock, ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, TimePicker24h } from "../../ui-v2";
import { cn } from "../../../../lib/cn";
import PreviewSlotsWizard from "./PreviewSlotsWizard";
import { DURACION_MIN, DURACION_MAX, duracionValida, leerDuracion } from "./wizardState";
import CamposRecordatorios from "./CamposRecordatorios";

/**
 * Paso 3 · Fechas y franjas horarias · v4
 *
 * v4 — añade botón "Gestionar bloqueos en el calendario" cuando estamos
 * en modo EDICIÓN (payload tiene id). Enlaza a /citas-tecnicas/calendario
 * con la campaña pre-filtrada vía query param.
 *
 * En modo CREACIÓN el botón no aparece (la campaña aún no existe).
 *
 * Requiere que el componente del calendario (pages/Calendario.jsx) lea
 * el query param `?campana_id=X` al montar y aplique el filtro. Ver
 * snippet en el README de la pieza.
 *
 * Rediseño (Fase 2):
 *  - Error 3: el botón miraba `payload.id`, que el reducer nunca rellena (el id va en state.id), y no
 *    salía nunca. Ahora quien edita pasa `campanaId`; el asistente no lo pasa y sigue sin botón.
 *    `onIrA(ruta)`, opcional: quien edita decide cómo salir (avisa si hay cambios sin guardar).
 *  - `sinRecordatorios`: Editar pinta los recordatorios en «Reglas» (CamposRecordatorios); el
 *    asistente los sigue viendo aquí.
 *  - A: la duración es un número libre de minutos (DURACION_MIN-DURACION_MAX, los límites del
 *    servidor) en vez de la lista cerrada, que con una campaña de 20 min enseñaba otro valor.
 */

const DIAS = [
  { num: 1, label: "Lunes",     corto: "Lun" },
  { num: 2, label: "Martes",    corto: "Mar" },
  { num: 3, label: "Miércoles", corto: "Mié" },
  { num: 4, label: "Jueves",    corto: "Jue" },
  { num: 5, label: "Viernes",   corto: "Vie" },
  { num: 6, label: "Sábado",    corto: "Sáb" },
  { num: 7, label: "Domingo",   corto: "Dom" },
];

function pad(n) { return String(n).padStart(2, "0"); }
function timeAMin(t) {
  if (!t) return 0;
  const [h, m] = String(t).split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}
function minATime(min) {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}:00`;
}
// Las franjas de un día, ordenadas, con su índice en la lista completa (_idx).
function franjasDeDia(franjas, dia) {
  return franjas
    .map((f, _idx) => ({ ...f, _idx }))
    .filter((f) => f.dia_semana === dia)
    .sort((a, b) => timeAMin(a.hora_inicio) - timeAMin(b.hora_inicio));
}
function normalizarHora(v) {
  if (!v) return "";
  return v.length === 5 ? `${v}:00` : v;
}

export default function Paso3Fechas({
  payload, errores = {}, dispatch, campanaId = null, onIrA = null, sinRecordatorios = false,
}) {

  const navigate = useNavigate();
  const franjas = useMemo(() => payload.franjas || [], [payload.franjas]);
  const idCampana = campanaId ?? payload.id ?? null;
  const esModoEdicion = !!idCampana;

  function setFranjas(nuevas) {
    dispatch({ type: "SET_FIELD", field: "franjas", value: nuevas });
  }
  function setCampo(campo, valor) {
    dispatch({ type: "SET_FIELD", field: campo, value: valor });
  }
  function franjasDelDia(dia) {
    return franjasDeDia(franjas, dia);
  }
  function toggleDia(dia) {
    const tieneAlgo = franjas.some((f) => f.dia_semana === dia);
    if (tieneAlgo) {
      setFranjas(franjas.filter((f) => f.dia_semana !== dia));
    } else {
      setFranjas([
        ...franjas,
        { dia_semana: dia, hora_inicio: "09:00:00", hora_fin: "13:00:00", orden: 0 },
      ]);
    }
  }
  function añadirFranja(dia) {
    const ya = franjasDelDia(dia);
    let hora_inicio = "09:00:00";
    if (ya.length > 0) {
      const ultima = ya[ya.length - 1];
      const nuevoIni = timeAMin(ultima.hora_fin) + 60;
      hora_inicio = nuevoIni < 23 * 60 ? minATime(nuevoIni) : "21:00:00";
    }
    const finMin = Math.min(timeAMin(hora_inicio) + 120, 23 * 60);
    setFranjas([
      ...franjas,
      { dia_semana: dia, hora_inicio, hora_fin: minATime(finMin), orden: ya.length },
    ]);
  }
  function actualizarFranja(idxGlobal, campo, valor) {
    const nuevas = franjas.slice();
    nuevas[idxGlobal] = { ...nuevas[idxGlobal], [campo]: normalizarHora(valor) };
    setFranjas(nuevas);
  }
  function eliminarFranja(idxGlobal) {
    setFranjas(franjas.filter((_, i) => i !== idxGlobal));
  }
  function aplicarHorarioComun({ diasObjetivo, franjasComun }) {
    if (!Array.isArray(diasObjetivo) || diasObjetivo.length === 0) return;
    if (!Array.isArray(franjasComun) || franjasComun.length === 0) return;
    const sinDiasObjetivo = franjas.filter((f) => !diasObjetivo.includes(f.dia_semana));
    const nuevasParaDias = [];
    diasObjetivo.forEach((dia) => {
      franjasComun.forEach((f, idx) => {
        nuevasParaDias.push({
          dia_semana: dia,
          hora_inicio: normalizarHora(f.hora_inicio),
          hora_fin: normalizarHora(f.hora_fin),
          orden: idx,
        });
      });
    });
    setFranjas([...sinDiasObjetivo, ...nuevasParaDias]);
  }

  function irACalendario() {
    if (!esModoEdicion) return;
    const destino = `/citas-tecnicas/calendario?campana_id=${idCampana}`;
    // Editar pasa onIrA para avisar si hay cambios sin guardar antes de salir.
    if (onIrA) onIrA(destino);
    else navigate(destino);
  }

  const erroresVisibles = useMemo(() => {
    const errs = [];
    if (!payload.fecha_inicio) errs.push("Falta fecha de inicio.");
    if (!payload.fecha_limite) errs.push("Falta fecha límite.");
    if (payload.fecha_inicio && payload.fecha_limite &&
        payload.fecha_inicio > payload.fecha_limite) {
      errs.push("La fecha de inicio es posterior a la fecha límite.");
    }
    if (payload.duracion_cita == null) errs.push("Falta duración por cita.");
    else if (!duracionValida(payload.duracion_cita)) {
      errs.push(`La duración por cita debe ser un número entero entre ${DURACION_MIN} y ${DURACION_MAX} minutos.`);
    }
    if (franjas.length === 0) {
      errs.push("Define franjas en al menos un día de la semana.");
    }
    for (const { num, label } of DIAS) {
      const lista = franjasDeDia(franjas, num);
      for (let i = 1; i < lista.length; i++) {
        if (timeAMin(lista[i].hora_inicio) < timeAMin(lista[i - 1].hora_fin)) {
          errs.push(
            `${label}: franjas solapadas (${lista[i-1].hora_inicio.slice(0,5)}-${lista[i-1].hora_fin.slice(0,5)} y ${lista[i].hora_inicio.slice(0,5)}-${lista[i].hora_fin.slice(0,5)}).`
          );
        }
      }
      for (const f of lista) {
        if (timeAMin(f.hora_inicio) >= timeAMin(f.hora_fin)) {
          errs.push(`${label}: una franja tiene hora inicio ≥ hora fin.`);
        }
      }
    }
    return errs;
  }, [payload, franjas]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Fecha inicio *
          </label>
          <input
            type="date"
            value={payload.fecha_inicio || ""}
            onChange={(e) => setCampo("fecha_inicio", e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Fecha límite *
          </label>
          <input
            type="date"
            value={payload.fecha_limite || ""}
            onChange={(e) => setCampo("fecha_limite", e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Duración por cita (min) *
          </label>
          <input
            type="number"
            inputMode="numeric"
            min={DURACION_MIN}
            max={DURACION_MAX}
            step={5}
            value={payload.duracion_cita ?? ""}
            onChange={(e) => setCampo("duracion_cita", leerDuracion(e.target.value))}
            aria-invalid={!duracionValida(payload.duracion_cita)}
            className={cn(
              "w-full border rounded-md px-3 py-2 text-sm",
              duracionValida(payload.duracion_cita) ? "border-slate-200" : "border-red-300"
            )}
          />
          <p className={cn("text-xs mt-1", duracionValida(payload.duracion_cita) ? "text-slate-400" : "text-red-600")}>
            Entre {DURACION_MIN} y {DURACION_MAX} minutos.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6 min-w-0">
          <HorarioComun onAplicar={aplicarHorarioComun} />

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-base font-semibold text-slate-900">
                Franjas horarias por día
              </h3>
              <span className="text-xs text-slate-500">Horas en 24h</span>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Habilita los días en que se atenderán citas y define las franjas
              horarias de cada uno.
            </p>

            <div className="space-y-3">
              {DIAS.map(({ num, label }) => (
                <DiaCard
                  key={num}
                  dia={num}
                  label={label}
                  franjas={franjasDelDia(num)}
                  onToggle={() => toggleDia(num)}
                  onAñadir={() => añadirFranja(num)}
                  onActualizar={actualizarFranja}
                  onEliminar={eliminarFranja}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 lg:self-start space-y-3">
          <PreviewSlotsWizard payload={payload} />

          {/* Enlace al calendario, solo en modo edición y con franjas */}
          {esModoEdicion && franjas.length > 0 && (
            <button
              type="button"
              onClick={irACalendario}
              className="w-full border border-slate-200 rounded-lg bg-white hover:border-primary hover:bg-primary/5 px-4 py-3 transition-colors group flex items-start gap-3 text-left"
            >
              <CalendarClock className="w-4 h-4 text-slate-400 group-hover:text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 group-hover:text-primary flex items-center justify-between gap-2">
                  Gestionar bloqueos por técnico
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-primary transition-transform group-hover:translate-x-0.5" />
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                  Los bloqueos por técnico se gestionan desde el calendario,
                  con esta campaña pre-filtrada.
                </p>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Fase 22 · #21 — recordatorios automáticos a usuarios sin cita (Editar los pone en «Reglas») */}
      {!sinRecordatorios && (
        <div className="pt-6 border-t border-slate-200">
          <CamposRecordatorios payload={payload} dispatch={dispatch} />
        </div>
      )}

      {erroresVisibles.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-0.5">
          {erroresVisibles.map((e, i) => (
            <div key={i}>• {e}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  (HorarioComun, DiaCard, FranjaRow — sin cambios desde v3 de 12g)
// ────────────────────────────────────────────────────────────────

function HorarioComun({ onAplicar }) {
  const [abierto, setAbierto] = useState(false);
  const [diasObjetivo, setDiasObjetivo] = useState([]);
  const [franjasComun, setFranjasComun] = useState([
    { hora_inicio: "09:00:00", hora_fin: "13:00:00" },
  ]);
  const [recienAplicado, setRecienAplicado] = useState(false);

  function toggleDiaObjetivo(dia) {
    setDiasObjetivo((prev) =>
      prev.includes(dia) ? prev.filter((d) => d !== dia) : [...prev, dia].sort()
    );
  }
  function actualizarFranja(idx, campo, valor) {
    setFranjasComun((prev) => {
      const nuevas = prev.slice();
      nuevas[idx] = { ...nuevas[idx], [campo]: normalizarHora(valor) };
      return nuevas;
    });
  }
  function añadirFranjaLocal() {
    const ultima = franjasComun[franjasComun.length - 1];
    let hora_inicio = "15:00:00";
    if (ultima) {
      const min = timeAMin(ultima.hora_fin) + 60;
      hora_inicio = min < 23 * 60 ? minATime(min) : "21:00:00";
    }
    const finMin = Math.min(timeAMin(hora_inicio) + 120, 23 * 60);
    setFranjasComun([...franjasComun, { hora_inicio, hora_fin: minATime(finMin) }]);
  }
  function eliminarFranjaLocal(idx) {
    setFranjasComun((prev) => prev.filter((_, i) => i !== idx));
  }
  function aplicar() {
    onAplicar({ diasObjetivo, franjasComun });
    setRecienAplicado(true);
    setTimeout(() => setRecienAplicado(false), 2500);
    setDiasObjetivo([]);
  }
  function cerrar() {
    setAbierto(false);
    setDiasObjetivo([]);
    setRecienAplicado(false);
  }

  const puedeAplicar = diasObjetivo.length > 0 && franjasComun.length > 0 &&
    franjasComun.every((f) =>
      f.hora_inicio && f.hora_fin && timeAMin(f.hora_inicio) < timeAMin(f.hora_fin)
    );

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Copy className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">
            Aplicar horario común a varios días
          </span>
          {recienAplicado && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <Check className="w-3 h-3" /> Aplicado
            </span>
          )}
        </div>
        {abierto ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {abierto && (
        <div className="px-4 pb-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 mt-3 mb-4">
            Define un horario y aplícalo a varios días de golpe.{" "}
            <strong>Reemplaza las franjas existentes</strong> de los días marcados.
          </p>

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Aplicar a estos días
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {DIAS.map(({ num, label, corto }) => {
                const sel = diasObjetivo.includes(num);
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => toggleDiaObjetivo(num)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                      sel
                        ? "bg-primary text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )}
                    title={label}
                  >
                    {corto}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Franjas horarias
            </label>
            <div className="space-y-1.5">
              {franjasComun.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <TimePicker24h
                    value={f.hora_inicio}
                    onChange={(v) => actualizarFranja(idx, "hora_inicio", v)}
                  />
                  <span className="text-slate-400 text-sm">→</span>
                  <TimePicker24h
                    value={f.hora_fin}
                    onChange={(v) => actualizarFranja(idx, "hora_fin", v)}
                  />
                  <div className="flex-1" />
                  {franjasComun.length > 1 && (
                    <button
                      type="button"
                      onClick={() => eliminarFranjaLocal(idx)}
                      className="text-slate-400 hover:text-red-600 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={añadirFranjaLocal}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:bg-primary/5 px-2.5 py-1.5 rounded-md transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Añadir franja
            </button>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={cerrar}
              className="text-xs text-slate-500 hover:text-slate-900 px-3 py-1.5"
            >
              Cerrar
            </button>
            <Button size="sm" onClick={aplicar} disabled={!puedeAplicar}>
              Aplicar a {diasObjetivo.length}{" "}
              {diasObjetivo.length === 1 ? "día" : "días"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DiaCard({ dia, label, franjas, onToggle, onAñadir, onActualizar, onEliminar }) {
  const habilitado = franjas.length > 0;
  return (
    <div className={cn(
      "border rounded-lg overflow-hidden transition-colors",
      habilitado ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/50"
    )}>
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={habilitado}
            onClick={onToggle}
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              habilitado ? "bg-primary" : "bg-slate-300"
            )}
          >
            <span className={cn(
              "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform",
              habilitado ? "translate-x-4" : "translate-x-0"
            )} />
          </button>
          <span className={cn("text-sm font-medium", habilitado ? "text-slate-900" : "text-slate-400")}>
            {label}
          </span>
        </div>
        {!habilitado && <span className="text-xs text-slate-400 italic">Sin trabajo</span>}
      </div>

      {habilitado && (
        <div className="p-3 space-y-2">
          {franjas.map((f) => (
            <FranjaRow
              key={f._idx}
              franja={f}
              onActualizar={(campo, valor) => onActualizar(f._idx, campo, valor)}
              onEliminar={() => onEliminar(f._idx)}
            />
          ))}
          <button
            type="button"
            onClick={onAñadir}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:bg-primary/5 px-2.5 py-1.5 rounded-md transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Añadir franja
          </button>
        </div>
      )}
    </div>
  );
}

function FranjaRow({ franja, onActualizar, onEliminar }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2">
      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <TimePicker24h
        value={franja.hora_inicio}
        onChange={(v) => onActualizar("hora_inicio", v)}
      />
      <span className="text-slate-400 text-sm">→</span>
      <TimePicker24h
        value={franja.hora_fin}
        onChange={(v) => onActualizar("hora_fin", v)}
      />
      <div className="flex-1" />
      <button
        type="button"
        onClick={onEliminar}
        className="text-slate-400 hover:text-red-600 p-1 rounded transition-colors"
        title="Eliminar franja"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
